import React from 'react';
import { useRouter } from 'next/router';
import Link from '@/components/link';
import { FAQ_URL } from '@/config/global-constants';
import useAuthentication from '@/hooks/use-authentication';
import useCustomerSupport from '@/hooks/use-customer-support/use-customer-support';
import useMe from '@/hooks/use-me';
import useSignOut from '@/hooks/use-sign-out';
import useTranslation from '@/hooks/use-translation';
import useAnalyticsWithSource from '@/hooks/use-analytics-with-source';

type NavLinksProps = {
  isSubscription: boolean;
  collapseNav?: () => void;
};

const NavLinks = ({ isSubscription, collapseNav }: NavLinksProps) => {
  const router = useRouter();
  const { t } = useTranslation('components.header');
  const { isAuthenticated } = useMe();
  const signOutMutation = useSignOut();
  const { showSupport } = useCustomerSupport();
  const handleAuth = useAuthentication();
  const { trackEvent, AnalyticsEvents } = useAnalyticsWithSource();

  const showHelp = !!window.Intercom?.booted;

  const handlePressSignOut = (event: React.MouseEvent) => {
    event.preventDefault();
    trackEvent(AnalyticsEvents.Navigation.NavSignOutButtonClicked);
    signOutMutation.mutate();
  };

  const handleSignUp = (event: React.MouseEvent) => {
    event.preventDefault();
    trackEvent(AnalyticsEvents.Navigation.NavSignUpButtonClicked);
    handleAuth();
  };

  const handleSupport = (event: React.MouseEvent) => {
    event.preventDefault();
    trackEvent(AnalyticsEvents.Navigation.NavSupportButtonClicked);
    showSupport();
    collapseNav?.();
  };

  return (
    <>
      {isSubscription && (
        <Link
          href="/"
          onPress={() =>
            trackEvent(AnalyticsEvents.Navigation.NavShortTermRentalClicked)
          }
          variant="paragraphMedium"
        >
          {t('navigation.short-term-rental')}
        </Link>
      )}
      <Link
        openNewWindow
        href="https://team.kyte.com/driver-partner/"
        variant="paragraphMedium"
      >
        {t('navigation.become-a-surfer')}
      </Link>
      <Link
        href={FAQ_URL}
        onPress={() => trackEvent(AnalyticsEvents.Navigation.NavFaqClicked)}
        openNewWindow
        prefetch
        variant="paragraphMedium"
      >
        {t('navigation.faq')}
      </Link>
      {isAuthenticated ? (
        <>
          <Link
            active={router.asPath === '/settings'}
            href="/settings"
            onPress={() =>
              trackEvent(AnalyticsEvents.Navigation.NavSettingsClicked)
            }
            prefetch
            variant="paragraphMedium"
          >
            {t('navigation.settings')}
          </Link>
          <Link
            active={router.asPath === '/invite-friends'}
            href="/invite-friends"
            onPress={() =>
              trackEvent(AnalyticsEvents.Navigation.NavReferralClicked)
            }
            prefetch
            variant="paragraphMedium"
          >
            {t('navigation.referrals')}
          </Link>
          <Link
            active={router.asPath === '/my-trips'}
            href="/my-trips"
            onPress={() =>
              trackEvent(AnalyticsEvents.Navigation.NavTripsClicked)
            }
            prefetch
            variant="paragraphMedium"
          >
            {t('navigation.my-trips')}
          </Link>
          {showHelp && (
            <Link href="#" onPress={handleSupport} variant="paragraphMedium">
              {t('navigation.help')}
            </Link>
          )}
          <Link
            href="/"
            onPress={handlePressSignOut}
            variant="paragraphMedium"
            testID="sign-up-link"
          >
            {t('navigation.sign-out')}
          </Link>
        </>
      ) : (
        <>
          <Link
            href="/"
            onPress={handleSignUp}
            variant="paragraphMedium"
            testID="sign-up-link"
          >
            {t('navigation.sign-up')}
          </Link>
          <Link
            active={router.asPath === '/settings'}
            href="/settings"
            onPress={() =>
              trackEvent(AnalyticsEvents.Navigation.NavSettingsClicked)
            }
            prefetch
            variant="paragraphMedium"
          >
            {t('navigation.settings')}
          </Link>
          {showHelp && (
            <Link href="#" onPress={handleSupport} variant="paragraphMedium">
              {t('navigation.help')}
            </Link>
          )}
        </>
      )}
    </>
  );
};

export default NavLinks;
